/**
 * Report Service
 * 處理所有與報表相關的 API 請求
 */

import {saveAs} from 'file-saver';
import {CrudService} from "../../core/common/crud-service";
import {loadMembershipPdf} from './membershipPdf';

/**
 * 報表服務類
 */
export class ReportService extends CrudService {

    /**
     * The base endpoint path for parameters
     */
    protected readonly basePath = '/api/report';

    /**
     *  new feature name: 會員資料
     *  old feature name: 會員主檔列印 pdf
     *  URL: /api/report/membership/exportMembershipPdf
     */
    async getMembershipPdf(request: any, signal?: AbortSignal): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            return await loadMembershipPdf(request, signal);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員郵寄名條列表
     *  old feature name: 會員郵寄名條列印
     * URL: /api/report/membership/exportMembershipNametagPdf
     */
    async getMembershipNametagPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipNametagPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員基本資料列表
     *  old feature name: 會員資料列印
     * URL: /api/report/membership/exportMembershipInfoPdf
     */
    async getMembershipInfoPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipInfoPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員產品列表
     *  old feature name: 會員產品查詢列印
     * /api/report/membership/exportMemberProductsPdf
     */
    async getMemberProductsPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberProductsPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員工廠名冊
     *  old feature name: 工廠會員名冊
     * /api/report/membership/exportMemberFactoryPdf
     */
    async getMemberFactoryPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberFactoryPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表名冊
     *  old feature name: 會員代表名冊
     * /api/report/membership/exportMemberDelegatePdf
     */
    async getMemberDelegatePdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberDelegatePdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表名條
     *  old feature name: 會員代表名條列印
     * /api/report/membership/exportMemberDelegateNametagPdf
     */
    async getMemberDelegateNametagPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberDelegateNametagPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員公司名冊
     *  old feature name: 公司會員名冊
     * /api/report/membership/exportMemberCompanyPdf
     */
    async getMemberCompanyPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberCompanyPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 全會員證書
     *  old feature name: 全會員證書套印
     * /api/report/membership/exportMembershipPaperPdf
     */
    async getMembershipPaperPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipPaperPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員網址、電子郵件列表
     *  old feature name: 網址郵件列印
     * /api/report/membership/exportMembershipWebsiteEmailPdf
     */
    async getMembershipWebsiteEmailPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipWebsiteEmailPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員入會日期列表
     *  old feature name: 會員入會日期列印
     * /api/report/membership/exportMembershipJoinDatePdf
     */
    async getMembershipJoinDatePdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipJoinDatePdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 停權會員列表
     *  old feature name: 停權會員檢視與列印
     * /api/report/membership/exportMembershipSuspensionPdf
     */
    async getMembershipSuspensionPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipSuspensionPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 退會會員列表
     *  old feature name: 退會會員檢視與列印
     * /api/report/membership/exportMembershipWithdrawalPdf
     */
    async getMembershipWithdrawalPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipWithdrawalPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表卡列印
     *  old feature name: 會員代表卡列印
     * /api/report/membership/exportMemberDelegateCardPdf
     */
    async getMemberDelegateCardPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberDelegateCardPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 實際產品列表
     *  old feature name: 2.3 產品主檔查詢列印
     * /api/report/product/exportProductsPdf
     */
    async getProductsPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('product/exportProductsPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品會員列表
     *  old feature name: 產品會員查詢列印
     * /api/report/product/exportProductMembersPdf
     */
    async getProductMembersPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('product/exportProductMembersPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品會員名條
     *  old feature name: 產品會員報表列印
     * /api/report/product/exportProductMembersNametagsPdf
     */
    async getProductMembersNametagsPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('product/exportProductMembersNametagsPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 認證會員基本資料列表
     *  old feature name: 會員認證列印
     * /api/report/certification/exportCertificationMembersPdf
     */
    async getCertificationMembersPdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('certification/exportCertificationMembersPdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員級數錯誤檢驗
     *  old feature name: 會員級數錯誤檢驗
     * /api/report/membership/exportMembershipLevelCheckPdf
     */
    async getMembershipLevelCheckPdf(): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customGetPdf('membership/exportMembershipLevelCheckPdf');
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員組織統計
     *  old feature name: 會員組織統計
     * /api/report/membership/exportMembershipOrganizationPdf
     */
    async getMembershipOrganizationPdf(): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customGetPdf('membership/exportMembershipOrganizationPdf');
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 比價證明書列印
     *  old feature name: 比價證明書列印
     * /api/report/membership/exportMembershipParityProvePdf
     */
    async getMembershipParityProvePdf(request: any): Promise<Blob> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipParityProvePdf', request);
            return result.blob;
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }


    /**
     *  new feature name: 會員資料
     *  old feature name: 會員主檔列印 pdf
     *  URL: /api/report/membership/exportMembershipPdf
     */
    async exportMembershipPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員郵寄名條列表
     *  old feature name: 會員郵寄名條列印
     * URL: /api/report/membership/exportMembershipNametagPdf
     */
    async exportMembershipNametagPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipNametagPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員基本資料列表
     *  old feature name: 會員資料列印
     * URL: /api/report/membership/exportMembershipInfoPdf
     */
    async exportMembershipInfoPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipInfoPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員產品列表
     *  old feature name: 會員產品查詢列印
     * /api/report/membership/exportMemberProductsPdf
     */
    async exportMemberProductsPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberProductsPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員工廠名冊
     *  old feature name: 工廠會員名冊
     * /api/report/membership/exportMemberFactoryPdf
     */
    async exportMemberFactoryPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberFactoryPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表名冊
     *  old feature name: 會員代表名冊
     * /api/report/membership/exportMemberDelegatePdf
     */
    async exportMemberDelegatePdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberDelegatePdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表名條
     *  old feature name: 會員代表名條列印
     * /api/report/membership/exportMemberDelegateNametagPdf
     */
    async exportMemberDelegateNametagPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberDelegateNametagPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員公司名冊
     *  old feature name: 公司會員名冊
     * /api/report/membership/exportMemberCompanyPdf
     */
    async exportMemberCompanyPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberCompanyPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 全會員證書
     *  old feature name: 全會員證書套印
     * /api/report/membership/exportMembershipPaperPdf
     */
    async exportMembershipPaperPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipPaperPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員網址、電子郵件列表
     *  old feature name: 網址郵件列印
     * /api/report/membership/exportMembershipWebsiteEmailPdf
     */
    async exportMembershipWebsiteEmailPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipWebsiteEmailPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員入會日期列表
     *  old feature name: 會員入會日期列印
     * /api/report/membership/exportMembershipJoinDatePdf
     */
    async exportMembershipJoinDatePdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipJoinDatePdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 停權會員列表
     *  old feature name: 停權會員檢視與列印
     * /api/report/membership/exportMembershipSuspensionPdf
     */
    async exportMembershipSuspensionPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipSuspensionPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 退會會員列表
     *  old feature name: 退會會員檢視與列印
     * /api/report/membership/exportMembershipWithdrawalPdf
     */
    async exportMembershipWithdrawalPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipWithdrawalPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表卡列印
     *  old feature name: 會員代表卡列印
     * /api/report/membership/exportMemberDelegateCardPdf
     */
    async exportMemberDelegateCardPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMemberDelegateCardPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 實際產品列表
     *  old feature name: 2.3 產品主檔查詢列印
     * /api/report/product/exportProductsPdf
     */
    async exportProductsPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('product/exportProductsPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品會員列表
     *  old feature name: 產品會員查詢列印
     * /api/report/product/exportProductMembersPdf
     */
    async exportProductMembersPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('product/exportProductMembersPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品會員名條
     *  old feature name: 產品會員報表列印
     * /api/report/product/exportProductMembersNametagsPdf
     */
    async exportProductMembersNametagsPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('product/exportProductMembersNametagsPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 認證會員基本資料列表
     *  old feature name: 會員認證列印
     * /api/report/certification/exportCertificationMembersPdf
     */
    async exportCertificationMembersPdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('certification/exportCertificationMembersPdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員級數錯誤檢驗
     *  old feature name: 會員級數錯誤檢驗
     * /api/report/membership/exportMembershipLevelCheckPdf
     */
    async exportMembershipLevelCheckPdf(): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customGetPdf('membership/exportMembershipLevelCheckPdf');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員組織統計
     *  old feature name: 會員組織統計
     * /api/report/membership/exportMembershipOrganizationPdf
     */
    async exportMembershipOrganizationPdf(): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customGetPdf('membership/exportMembershipOrganizationPdf');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 比價證明書列印
     *  old feature name: 比價證明書列印
     * /api/report/membership/exportMembershipParityProvePdf
     */
    async exportMembershipParityProvePdf(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipParityProvePdf', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }







    /**
     *  new feature name: 會員資料
     *  old feature name: 會員主檔列印 pdf
     *  URL: /api/report/membership/exportMembershipExcel
     */
    async exportMembershipExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員郵寄名條列表
     *  old feature name: 會員郵寄名條列印
     * URL: /api/report/membership/exportMembershipNametagExcel
     */
    async exportMembershipNametagExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipNametagExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員基本資料列表
     *  old feature name: 會員資料列印
     * URL: /api/report/membership/exportMembershipInfoExcel
     */
    async exportMembershipInfoExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipInfoExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員產品列表
     *  old feature name: 會員產品查詢列印
     * /api/report/membership/exportMemberProductsExcel
     */
    async exportMemberProductsExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMemberProductsExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員工廠名冊
     *  old feature name: 工廠會員名冊
     * /api/report/membership/exportMemberFactoryExcel
     */
    async exportMemberFactoryExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMemberFactoryExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表名冊
     *  old feature name: 會員代表名冊
     * /api/report/membership/exportMemberDelegateExcel
     */
    async exportMemberDelegateExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMemberDelegateExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表名條
     *  old feature name: 會員代表名條列印
     * /api/report/membership/exportMemberDelegateNametagExcel
     */
    async exportMemberDelegateNametagExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMemberDelegateNametagExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員公司名冊
     *  old feature name: 公司會員名冊
     * /api/report/membership/exportMemberCompanyExcel
     */
    async exportMemberCompanyExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMemberCompanyExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 全會員證書
     *  old feature name: 全會員證書套印
     * /api/report/membership/exportMembershipPaperExcel
     */
    async exportMembershipPaperExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipPaperExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員網址、電子郵件列表
     *  old feature name: 網址郵件列印
     * /api/report/membership/exportMembershipWebsiteEmailExcel
     */
    async exportMembershipWebsiteEmailExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipWebsiteEmailExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員入會日期列表
     *  old feature name: 會員入會日期列印
     * /api/report/membership/exportMembershipJoinDateExcel
     */
    async exportMembershipJoinDateExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipJoinDateExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 停權會員列表
     *  old feature name: 停權會員檢視與列印
     * /api/report/membership/exportMembershipSuspensionExcel
     */
    async exportMembershipSuspensionExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipSuspensionExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 退會會員列表
     *  old feature name: 退會會員檢視與列印
     * /api/report/membership/exportMembershipWithdrawalExcel
     */
    async exportMembershipWithdrawalExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMembershipWithdrawalExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員代表卡列印
     *  old feature name: 會員代表卡列印
     * /api/report/membership/exportMemberDelegateCardExcel
     */
    async exportMemberDelegateCardExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('membership/exportMemberDelegateCardExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 實際產品列表
     *  old feature name: 2.3 產品主檔查詢列印
     * /api/report/product/exportProductsExcel
     */
    async exportProductsExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('product/exportProductsExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品會員列表
     *  old feature name: 產品會員查詢列印
     * /api/report/product/exportProductMembersExcel
     */
    async exportProductMembersExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('product/exportProductMembersExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品會員名條
     *  old feature name: 產品會員報表列印
     * /api/report/product/exportProductMembersNametagsExcel
     */
    async exportProductMembersNametagsExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('product/exportProductMembersNametagsExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 認證會員基本資料列表
     *  old feature name: 會員認證列印
     * /api/report/certification/exportCertificationMembersExcel
     */
    async exportCertificationMembersExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostExcel('certification/exportCertificationMembersExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員級數錯誤檢驗
     *  old feature name: 會員級數錯誤檢驗
     * /api/report/membership/exportMembershipLevelCheckExcel
     */
    async exportMembershipLevelCheckExcel(): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customGetExcel('membership/exportMembershipLevelCheckExcel');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員組織統計
     *  old feature name: 會員組織統計
     * /api/report/membership/exportMembershipOrganizationExcel
     */
    async exportMembershipOrganizationExcel(): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customGetExcel('membership/exportMembershipOrganizationExcel');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 比價證明書列印
     *  old feature name: 比價證明書列印
     * /api/report/membership/exportMembershipParityProveExcel
     */
    async exportMembershipParityProveExcel(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostPdf('membership/exportMembershipParityProveExcel', request);
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }



    /**
     *  new feature name: 會員資料
     *  old feature name: 會員資料
     * /api/report/committee/exportCommitteCsv
     */
    async exportCommitteCsv(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('committee/exportCommitteCsv', request, 'csv');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 電子郵件
     *  old feature name: 電子郵件
     * /api/report/committee/exportCommitteTxt
     */
    async exportCommitteTxt(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('committee/exportCommitteTxt', request, 'txt');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員資料
     *  old feature name: 會員資料
     * /api/report/event/exportEventCsv
     */
    async exportEventCsv(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('event/exportEventCsv', request, 'csv');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 電子郵件
     *  old feature name: 電子郵件
     * /api/report/event/exportEventTxt
     */
    async exportEventTxt(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('event/exportEventTxt', request, 'txt');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 傳真
     *  old feature name: 傳真
     * /api/report/event/exportEventFaxCsv
     */
    async exportEventFaxCsv(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('event/exportEventFaxCsv', request, 'csv');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員傳真資料列印
     *  old feature name: 會員傳真資料列印
     * /api/report/membership/exportMemberFaxCsv
     */
    async exportMemberFaxCsv(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('membership/exportMemberFaxCsv', request, 'csv');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 會員主檔匯出
     *  old feature name: 會員主檔匯出
     * /api/report/membership/exportMemberMainFile
     */
    async exportMemberMainFile(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('membership/exportMemberMainFile', request, 'txt');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     *  new feature name: 產品對照檔匯出
     *  old feature name: 產品對照檔匯出
     * /api/report/membership/exportProductMainFile
     */
    async exportProductMainFile(request: any): Promise<any> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const result = await this.customPostFile('membership/exportProductMainFile', request, 'txt');
            saveAs(result.blob, result.fileName);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }
















    /**
     * 獲取會員列表 PDF 報表 - 從第一個端點獲取
     * 使用 arraybuffer 類型來處理二進制數據
     * 對應 API: GET /api/report/membership/members/pdf
     */
    async getMembershipPdfTest(): Promise<ArrayBuffer> {
        try {
            // 使用 responseType: 'arraybuffer' 來接收二進制數據
            return this.customGetArraybuffer('membership/members/pdf');
        } catch (error) {
            console.error('獲取會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     * 直接下載會員列表 PDF 報表
     * 對應 API: GET /api/report/membership/members/exportMembersPdf
     * 此端點設計為直接觸發檔案下載
     */
    async exportMembersPdfTest(filename: string = 'exported.pdf'): Promise<void> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const blob = await this.customGetPdf('membership/members/exportMembersPdf');
            saveAs(blob, filename);
        } catch (error) {
            console.error('下載會員PDF報表失敗:', error);
            throw error;
        }
    }

    /**
     * 直接下載會員列表 Excel 檔案
     * 對應 API: GET /api/report/membership/members/exportMembersExcel
     * 此端點設計為直接觸發檔案下載
     */
    async exportMembersExcelTest(filename: string = 'exported.xlsx'): Promise<void> {
        try {
            // 使用 responseType: 'blob' 來處理檔案下載
            const blob = await this.customGetExcel('membership/members/exportMembersExcel');
            saveAs(blob, filename);
        } catch (error) {
            console.error('下載會員Excel報表失敗:', error);
            throw error;
        }
    }
}

// 創建並導出 ReportService 實例
const reportService = new ReportService();

export {reportService};
